import { useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import { useForm } from 'react-hook-form';

import { t } from 'i18next';
import { InferType } from 'yup';
import { editPatientSchema } from '../schemas';
import { yupResolver } from '@hookform/resolvers/yup';
import {
  useEditPatientProfileMutation,
  usePatientProfileQuery,
} from '@common/cache';
import { GENDER_TYPE } from '@common/types';
import dayjs from 'dayjs';
import { formatApiError } from '@common/errors/error-formatting.ts';
import { useMediaQuery } from '@mui/material';

type IFormShape = InferType<typeof editPatientSchema>;

const initialValues: IFormShape = {
  first_name: '',
  last_name: '',
  birthdate: '',
  gender: GENDER_TYPE.MALE,
  account_status: 'Active',
  referral_source: 'Unknown',
  respite_eligible: 'Unknown',
};

export const useEditProfile = (patient_id: string) => {
  const patientProfile = usePatientProfileQuery({ patient_id });
  const formMethods = useForm<IFormShape>({
    mode: 'onChange',
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    resolver: yupResolver<IFormShape, any, any>(editPatientSchema),
    defaultValues: initialValues,
  });

  const {
    handleSubmit,
    formState: { isSubmitting, isDirty, isValid },
    reset,
    getValues,
    trigger,
  } = formMethods;

  const { mutateAsync, isPending } = useEditPatientProfileMutation({
    patient_id,
  });
  const [isOpen, setIsOpen] = useState(false);
  const [isSaveChangesModalOpen, setIsSaveChangesModalOpen] = useState(false);
  const [isConfirmModalOpen, setIsConfirmModalOpen] = useState(false);

  const isXSMSizeDP = useMediaQuery('(max-height: 768px)');
  const isSMSizeDP = useMediaQuery('(max-height: 900px)');
  const isMDSizeDP = useMediaQuery('(max-height: 1080px)');

  const genderOptions = [
    { value: GENDER_TYPE.MALE, label: t('patients.addModal.gender.male') },
    { value: GENDER_TYPE.FEMALE, label: t('patients.addModal.gender.female') },
    {
      value: GENDER_TYPE.UNKNOWN,
      label: t('patients.addModal.gender.unknown'),
    },
  ];

  const referralSourceOptions = [
    'ACC',
    'Allen Flores',
    'ARC',
    'Community marketer',
    'DTC',
    'GAIT',
    'GUIDE event',
    'Josh Allen',
    'Partner referral',
    'Premier',
    'Respite partner',
    'SNF/Rehab',
    'Stride Physical Therapy',
    'Social media',
    'Trilogy',
    'Unknown',
    'Website',
    'Wellness coordinator',
  ];

  const sourceDetailOptions = [
    'Call',
    'Email',
    'Fax',
    'List - Algorithm',
    'List - Confirmed Dx',
    'List - Suspected Dx',
    'List - Unknown Dx',
    'Referral - Provider',
    'Referral - Self/Family',
    'Slack',
    'Text/SMS',
  ];
  const ineligibilityReasons = [
    'Due to the tier',
    'Due to FAST score',
    'No ZBI Information',
  ];

  const accountStatusOptions = ['Active', 'Inactive'];
  const reasonInactiveStatus = [
    'Deceased',
    'Transferred',
    '0 Days on Service',
    'Was GUIDE Only',
    'Not Seen in over 365 Days',
    'Declined Services',
    'No Provider Available',
  ];

  const handleClose = () => {
    if (isDirty) {
      setIsSaveChangesModalOpen(true);
    } else {
      setIsOpen(false);
    }
  };

  const handleOnClick = () => {
    setIsOpen(true);
  };

  const submitHandler = async (values: IFormShape) => {
    if (
      values.account_status === 'Inactive' &&
      !isConfirmModalOpen &&
      patientProfile?.data?.account_status !== 'Inactive'
    ) {
      return setIsConfirmModalOpen(true);
    }
    try {
      await mutateAsync({
        first_name: values.first_name,
        middle_name: values.middle_name,
        last_name: values.last_name,
        preferred_name: values.preferred_name || null,
        has_preferred_name: !!values.preferred_name,
        gender: values.gender,
        account_status: values.account_status,
        birthdate: dayjs(values.birthdate).format('YYYY-MM-DD'),
        referral_source: values.referral_source,
        referral_source_detail: values.source_detail,
        respite_eligible: values.respite_eligible,
        respite_ineligibility_reason: values.respite_ineligibility_reason,
        ...((values.referral_source === 'Respite partner' ||
          values.respite_eligible) && {
          respite_partner_id: values.respite_partner_id || null,
        }),
        ...(values.inactivity_reason && {
          inactivity_reason: values.inactivity_reason as
            | 'Deceased'
            | 'Transferred'
            | '0 Days on Service'
            | 'Was GUIDE Only'
            | 'Not Seen in over 365 Days'
            | 'Declined Services'
            | 'No Provider Available',
        }),
        ...(values.inactivity_date && {
          inactivity_date: dayjs(values.inactivity_date).format('YYYY-MM-DD'),
        }),
        inactivity_date_approximate:
          values.inactivity_date_approximate ?? false,
      });

      toast.success('Patient edited successfully');
      setIsSaveChangesModalOpen(false);
      setIsOpen(false);
      setIsConfirmModalOpen(false);
      reset(values);
    } catch (error) {
      toast.error(formatApiError(error));
    }
  };

  const onSubmit = handleSubmit(submitHandler);

  const handleCloseSaveChangesModal = () => {
    setIsSaveChangesModalOpen(false);
  };

  const handleDiscardChanges = () => {
    setIsSaveChangesModalOpen(false);
    setIsOpen(false);
    reset();
  };

  const handleSaveChanges = async () => {
    await onSubmit();
  };

  useEffect(() => {
    if (patientProfile.data) {
      const {
        first_name,
        middle_name,
        last_name,
        gender,
        birthdate,
        referral_source,
        referral_source_detail,
        respite_partner_id,
        account_status,
        inactivity_reason,
        inactivity_date,
        inactivity_date_approximate,
        respite_eligible,
        respite_ineligibility_reason,
        preferred_name,
      } = patientProfile.data;

      reset({
        first_name,
        middle_name,
        last_name,
        preferred_name: preferred_name || '',
        respite_eligible,
        source_detail: referral_source_detail,
        gender: gender as GENDER_TYPE,
        account_status,
        birthdate: birthdate,
        referral_source,
        respite_partner_id,
        respite_ineligibility_reason,
        inactivity_reason: inactivity_reason || '',
        inactivity_date_approximate: !!inactivity_date_approximate,
        inactivity_date,
      });
      trigger();
    }
  }, [patientProfile.data]);

  return {
    formMethods,
    onSubmit,
    isSubmitting: isSubmitting || isPending,
    isOpen,
    submitHandler,
    handleSubmit,
    handleClose,
    handleOnClick,
    genderOptions,
    referralSourceOptions: referralSourceOptions.map(o => ({
      value: o,
      label: o,
    })),
    sourceDetailOptions: sourceDetailOptions.map(o => ({
      value: o,
      label: o,
    })),
    accountStatusOptions: accountStatusOptions.map(o => ({
      value: o,
      label: o,
    })),
    reasonInactiveStatusOptions: reasonInactiveStatus.map(o => ({
      value: o,
      label: o,
    })),
    ineligibilityReasons: ineligibilityReasons.map(o => ({
      value: o,
      label: o,
    })),
    isSaveChangesModalOpen,
    handleCloseSaveChangesModal,
    handleSaveChanges,
    handleDiscardChanges,
    isValid,
    isEditSubmitting: isPending,
    isConfirmModalOpen,
    setIsConfirmModalOpen,
    modalHeight: isXSMSizeDP
      ? 660
      : isSMSizeDP
        ? 770
        : isMDSizeDP
          ? 910
          : undefined,
    showWarningBanner:
      patientProfile?.data?.account_status !== 'Inactive' &&
      getValues('account_status') === 'Inactive',
  };
};
